import { Checkbox, Col, DatePicker, Form, FormInstance, Radio } from 'antd'

import { requiredRules } from 'src/app-constants'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  form: FormInstance
}

export const NeurologicalSymptomsCard = ({ form }: Props) => {
  const { t } = useLocales()

  const hasNeurological =
    Form.useWatch(['form', 'symptoms', 'neurological', 'status'], form) ===
    'yes'

  const hasPalsiesParalysis = Form.useWatch(
    ['form', 'symptoms', 'neurological', 'palsiesParalysis', 'isSelected'],
    form
  )

  const hasMuscleContractures = Form.useWatch(
    ['form', 'symptoms', 'neurological', 'muscleContractures', 'isSelected'],
    form
  )

  return (
    <>
      <Col span={24}>
        <Form.Item
          name={['form', 'symptoms', 'neurological', 'status']}
          rules={requiredRules}
          label={`3. ${t('epidemiology.neurological')}`}
        >
          <Radio.Group>
            <Radio value="dontKnow">{t('epidemiology.unknown')}</Radio>
            <Radio value="no">{t('common.no')}</Radio>
            <Radio value="yes">{t('common.yes')}</Radio>
          </Radio.Group>
        </Form.Item>
      </Col>

      {hasNeurological && (
        <>
          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'symptoms',
                'neurological',
                'consciousnessDisorders',
                'isSelected'
              ]}
              valuePropName="checked"
            >
              <Checkbox>1. Tulburări de conștiință</Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'symptoms',
                'neurological',
                'meningealSigns',
                'isSelected'
              ]}
              valuePropName="checked"
            >
              <Checkbox>2. Semne meningeale</Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'symptoms',
                'neurological',
                'convulsionsInvoluntaryMovements',
                'isSelected'
              ]}
              valuePropName="checked"
            >
              <Checkbox>3. Convulsii / mișcări involuntare</Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'symptoms',
                'neurological',
                'hydrophobia',
                'isSelected'
              ]}
              valuePropName="checked"
            >
              <Checkbox>4. Hidrofobie</Checkbox>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'symptoms',
                'neurological',
                'palsiesParalysis',
                'isSelected'
              ]}
              valuePropName="checked"
            >
              <Checkbox>5. Pareze / paralizii</Checkbox>
            </Form.Item>
          </Col>

          {hasPalsiesParalysis && (
            <>
              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'palsiesParalysis',
                    'appearanceDate',
                    'value'
                  ]}
                  label="Data apariției"
                >
                  <DatePicker
                    className="full-width"
                    format={dayMonthYearFormatWithDot}
                    disabledDate={disabledFutureDate}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'palsiesParalysis',
                    'acute',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>5.1 Acute</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'palsiesParalysis',
                    'flaccid',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>5.2 Flasce</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'palsiesParalysis',
                    'spastic',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>5.3 Spastice</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'palsiesParalysis',
                    'limbParalysis',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>5.4 Paralizia membrelor</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'palsiesParalysis',
                    'facialParalysis',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>5.5 Paralizie facială</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'palsiesParalysis',
                    'trunkParalysis',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>5.6 Paralizia trunchiului</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'palsiesParalysis',
                    'symmetric',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>5.7 Simetrice</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'palsiesParalysis',
                    'asymmetric',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>5.8 Asimetrice</Checkbox>
                </Form.Item>
              </Col>
            </>
          )}

          <Col span={8}>
            <Form.Item
              name={[
                'form',
                'symptoms',
                'neurological',
                'muscleContractures',
                'isSelected'
              ]}
              valuePropName="checked"
            >
              <Checkbox>6. Contracturi musculare</Checkbox>
            </Form.Item>
          </Col>

          {hasMuscleContractures && (
            <>
              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'muscleContractures',
                    'trism',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>6.1 Trism</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'muscleContractures',
                    'neckRigidity',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>6.2 Rigiditatea cefei</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'muscleContractures',
                    'trunkMuscles',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>6.3 Mușchii trunchiului</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name={[
                    'form',
                    'symptoms',
                    'neurological',
                    'muscleContractures',
                    'limbMuscles',
                    'isSelected'
                  ]}
                  valuePropName="checked"
                >
                  <Checkbox>6.4 Mușchii membrelor</Checkbox>
                </Form.Item>
              </Col>
            </>
          )}
        </>
      )}
    </>
  )
}
